import React, { useMemo } from 'react';
import { Box, Container, Header, Spinner } from '@amzn/awsui-components-core';
import type Highcharts from 'highcharts';
import { HighchartsChart } from '../charts/HighchartsChart';
import { useMultiLeaderFreetext } from '../useConnectionsData';
import { useTheme } from 'src/components/context/ThemeContextProvider';

interface SentimentDeepDiveProps {
  leaders: Array<{ login: string; name: string }>;
  metricPeriod: string;
  startDate: string;
  periodLabel?: string;
  infoLink?: React.ReactNode;
}

const TOP_N = 15;
const NEG_COLOR = '#c62828';
const POS_COLOR = '#1e7d32';

// Per-bar hover context (pre-formatted label strings so Highcharts' pointFormat
// templating can render them directly -- no formatter callback needed). Carries BOTH
// sentiments for a (leader, topic) so the tooltip shows the full P/N + sample breakdown
// that used to live in the standalone topics table.
interface BarMeta {
  leaderName: string;
  topicName: string;
  sampleSizeLabel: string;
  posPctLabel: string;
  negPctLabel: string;
}

interface BarPoint {
  label: string;
  value: number;
  meta: BarMeta;
}

// Accumulator per (leader, topic) holding raw values before we derive the labels.
interface TopicAgg {
  leaderName: string;
  topicName: string;
  sampleSize: number | null;
  posPct: number | null;
  negPct: number | null;
  posCount: number | null;
  negCount: number | null;
}

const fmtPct = (x: number | null | undefined): string => (x == null ? '-' : `${(x * 100).toFixed(1)}%`);

const buildBarOptions = (rows: BarPoint[], color: string, axisColor: string): Highcharts.Options => ({
  chart: { type: 'bar' },
  xAxis: {
    categories: rows.map((r) => r.label),
    labels: { style: { fontSize: '12px', color: axisColor, fontWeight: '500' } },
    lineWidth: 1
  },
  yAxis: {
    min: 0,
    allowDecimals: false,
    title: { text: 'Responses', style: { fontSize: '13px' } },
    gridLineWidth: 1
  },
  // Plain-text tooltip via pointFormat templating (this vendored Highcharts build renders
  // <br/> line breaks but not other HTML tags -- see project notes). Folds the old topics
  // table's sample size + positive%/negative% into the hover.
  tooltip: {
    headerFormat: '',
    pointFormat:
      '{point.custom.topicName} - {point.custom.leaderName}<br/>' +
      'Sample size: {point.custom.sampleSizeLabel}<br/>' +
      'Positive: {point.custom.posPctLabel}  ·  Negative: {point.custom.negPctLabel}<br/>' +
      'Responses (this bar): {point.y}'
  },
  plotOptions: {
    bar: {
      borderRadius: 2,
      borderWidth: 0,
      pointWidth: 14,
      color,
      dataLabels: {
        enabled: true,
        inside: false,
        color: axisColor,
        style: { fontSize: '11px', fontWeight: '600', textOutline: 'none', color: axisColor }
      }
    }
  },
  legend: { enabled: false },
  series: [{ type: 'bar', name: 'Responses', data: rows.map((r) => ({ y: r.value, custom: r.meta })) }]
});

/**
 * Sentiment Deep Dive - two horizontal bar charts (negative / positive) where
 * each bar is a "<Leader> · <Topic>" pair sized by response count, sorted
 * descending and capped at the top N. Fed by a fan-out freetext query across
 * the selected leader + their directs.
 *
 * The former standalone "Freetext Sentiment Topics" table was consolidated in here:
 * each bar's hover tooltip now reports sample size + positive%/negative% + response count
 * for that (leader, topic).
 */
export const SentimentDeepDive: React.FC<SentimentDeepDiveProps> = ({ leaders, metricPeriod, startDate, periodLabel, infoLink }) => {
  const { isDarkMode } = useTheme();
  const axisColor = isDarkMode ? '#e8ecff' : '#1a2236';
  const { data, loading, error } = useMultiLeaderFreetext(leaders, metricPeriod, startDate || null);

  const { negative, positive } = useMemo(() => {
    const rows = data || [];
    // Collapse the per-sentiment source rows into one record per (leader, topic) carrying
    // both sides, so a negative bar can still show the topic's positive % (and vice versa).
    const grp = new Map<string, TopicAgg>();
    rows.forEach((r) => {
      const key = `${r.leaderLogin}|${r.topicId}`;
      const g: TopicAgg = grp.get(key) || {
        leaderName: r.leaderName,
        topicName: r.topicName,
        sampleSize: null,
        posPct: null,
        negPct: null,
        posCount: null,
        negCount: null
      };
      if (r.sentiment === 'POSITIVE') {
        g.posCount = r.responseCount;
        g.posPct = r.metricValue;
      } else if (r.sentiment === 'NEGATIVE') {
        g.negCount = r.responseCount;
        g.negPct = r.metricValue;
      }
      if (g.sampleSize == null && r.sampleSize != null) g.sampleSize = r.sampleSize;
      grp.set(key, g);
    });

    const toBars = (sentiment: 'NEGATIVE' | 'POSITIVE'): BarPoint[] =>
      Array.from(grp.values())
        .map((g) => ({
          label: `${g.leaderName} · ${g.topicName}`,
          value: (sentiment === 'NEGATIVE' ? g.negCount : g.posCount) ?? 0,
          meta: {
            leaderName: g.leaderName,
            topicName: g.topicName,
            sampleSizeLabel: g.sampleSize == null ? '-' : String(g.sampleSize),
            posPctLabel: fmtPct(g.posPct),
            negPctLabel: fmtPct(g.negPct)
          }
        }))
        .filter((b) => b.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, TOP_N)
        // Highcharts bar renders bottom-up; reverse so the largest sits on top.
        .reverse();

    return { negative: toBars('NEGATIVE'), positive: toBars('POSITIVE') };
  }, [data]);

  const meta = periodLabel ? <span style={{ fontSize: '12px', color: 'var(--awsui-color-text-body-secondary)' }}>{periodLabel}</span> : undefined;

  const renderChart = (rows: BarPoint[], color: string, emptyLabel: string) => {
    if (loading) {
      return (
        <Box textAlign="center" padding="xl">
          <Spinner size="big" /> Loading...
        </Box>
      );
    }
    if (error) {
      return (
        <Box color="text-status-error" padding="m">
          {error.message}
        </Box>
      );
    }
    if (rows.length === 0) {
      return (
        <Box color="text-body-secondary" padding="m">
          {emptyLabel}
        </Box>
      );
    }
    return <HighchartsChart options={buildBarOptions(rows, color, axisColor)} height={Math.max(320, rows.length * 26 + 80)} />;
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '16px' }}>
      <Container
        header={
          <Header variant="h2" description={meta} info={infoLink}>
            💬 Sentiment Deep Dive - Negative Topics
          </Header>
        }
      >
        {renderChart(negative, NEG_COLOR, 'No negative-sentiment topics for this leader set and period.')}
      </Container>
      <Container
        header={
          <Header variant="h2" description={meta}>
            💚 Sentiment Deep Dive - Positive Topics
          </Header>
        }
      >
        {renderChart(positive, POS_COLOR, 'No positive-sentiment topics for this leader set and period.')}
      </Container>
    </div>
  );
};
